/**
 * SupportSessionBanner.tsx
 * ----------------------------------------------------------------------------
 * Banner global, no topo da aplicação, exibido SEMPRE que o operador WL
 * logado estiver dentro de uma "Sessão de Suporte" em algum tenant cliente.
 *
 * Funcionalidade:
 *   - Mostra nome do tenant alvo + tempo restante
 *   - Botão para encerrar a sessão imediatamente
 *   - Botão "Acessar" quando o operador está fora de /tenant
 *   - Botão de minimizar: colapsa a barra em uma pílula flutuante
 *   - Atualiza countdown a cada 1s; some sozinho quando expira
 *
 * RELEASE: 2026-08-13 v3.0.0 - A barra deixa de ser "sticky" (não sobrepõe mais o header do app).
 *                              Agora ocupa o topo do documento em fluxo normal, publica sua altura
 *                              real na CSS var global `--support-banner-h` (via ResizeObserver) para
 *                              que os layouts descontem esse espaço, ficou mais compacta e ganhou
 *                              botão de minimizar (pílula flutuante, estado em localStorage).
 * RELEASE: 2026-05-30 v2.2.0 - Encerrar agora faz hard reload para "/wl/tenants",
 *                              garantindo reidratação total do AuthProvider e
 *                              limpeza do currentTenant que foi trocado durante
 *                              a sessão de suporte (provisionamento temporário).
 * RELEASE: 2026-05-30 v2.1.0 - Adiciona botão "Acessar" que leva o operador
 *                              de volta para "/tenant" quando ele está em
 *                              qualquer rota fora do app do tenant (ex: /wl/*).
 * RELEASE: 2026-05-30 v2.0.0 - Removido badge de escopo (Somente leitura/Edição).
 * RELEASE: 2026-05-30 v1.0.0 - Versão inicial.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ShieldAlert, LogOut, LogIn, Loader2, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useActiveSupportSession,
  useEndSupportSession,
} from "@/hooks/wl/useSupportSession";
import { describeWlError } from "@/hooks/wl/useWlErrorToast";

/** Chave de persistência do estado minimizado (por navegador/usuário local). */
const COLLAPSED_STORAGE_KEY = "wl_support_banner_collapsed";
/** Nome da CSS var global consumida pelos layouts (.app-viewport / .app-viewport-min). */
const BANNER_HEIGHT_VAR = "--support-banner-h";

function formatRemaining(ms: number): string {
  if (ms <= 0) return "00:00";
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** Lê o estado minimizado do localStorage de forma tolerante a falhas. */
function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function SupportSessionBanner() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: session } = useActiveSupportSession();
  const end = useEndSupportSession();
  const [now, setNow] = useState(() => Date.now());
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  const barRef = useRef<HTMLDivElement | null>(null);

  // Mostra o botão "Acessar" sempre que o operador NÃO estiver dentro do app do tenant.
  const showEnterButton = !location.pathname.startsWith("/tenant");

  // Tick a cada 1s só quando há sessão ativa
  useEffect(() => {
    if (!session) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [session]);

  // Publica a altura real da barra na CSS var global. Quando não há sessão
  // (ou a barra está minimizada), a var volta para 0px e o app usa a tela toda.
  const isBarVisible = Boolean(session) && !collapsed;
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!isBarVisible) {
      root.style.setProperty(BANNER_HEIGHT_VAR, "0px");
      return () => root.style.removeProperty(BANNER_HEIGHT_VAR);
    }

    const el = barRef.current;
    if (!el) return;

    const apply = () => {
      root.style.setProperty(BANNER_HEIGHT_VAR, `${Math.round(el.offsetHeight)}px`);
    };
    apply();

    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty(BANNER_HEIGHT_VAR);
    };
  }, [isBarVisible]);

  // Persiste o estado minimizado
  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSED_STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const handleEnd = useCallback(async () => {
    try {
      await end.mutateAsync();
      toast({
        title: "Sessão de suporte encerrada",
        description: "Você não está mais operando dentro do tenant cliente.",
      });
      // Hard reload para /wl/tenants — wl_end_support_session restaurou
      // user_active_tenant para o tenant WL original do operador.
      try {
        localStorage.removeItem("currentTenantId");
      } catch {
        /* ignore */
      }
      window.location.assign("/wl/tenants");
    } catch (e: unknown) {
      toast({ variant: "destructive", title: "Falha ao encerrar sessão", description: describeWlError(e) });
    }
  }, [end, toast]);

  if (!session) return null;

  const remainingMs = new Date(session.expires_at).getTime() - now;
  const remainingLabel = formatRemaining(remainingMs);

  // ---------------------------------------------------------------------------
  // Estado minimizado: pílula flutuante discreta no canto inferior direito
  // ---------------------------------------------------------------------------
  if (collapsed) {
    return (
      <div
        className="fixed bottom-4 right-4 z-[60] flex items-center gap-1.5 rounded-full border border-amber-600 bg-amber-500 px-2.5 py-1 text-xs text-white shadow-lg"
        role="alert"
      >
        <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
        <span className="font-mono tabular-nums">{remainingLabel}</span>
        <button
          type="button"
          onClick={toggleCollapsed}
          className="rounded-full p-0.5 hover:bg-white/20"
          title="Expandir barra da sessão de suporte"
          aria-label="Expandir barra da sessão de suporte"
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={handleEnd}
          disabled={end.isPending}
          className="rounded-full p-0.5 hover:bg-white/20 disabled:opacity-60"
          title="Encerrar sessão de suporte"
          aria-label="Encerrar sessão de suporte"
        >
          {end.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <LogOut className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Barra compacta em fluxo normal (empurra o app para baixo, não sobrepõe)
  // ---------------------------------------------------------------------------
  return (
    <div
      ref={barRef}
      className="relative z-[60] w-full border-b border-amber-600 bg-amber-500 text-white shadow-sm"
      role="alert"
    >
      <div className="mx-auto flex max-w-[1600px] flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-1 text-[11px] sm:text-xs">
        <div className="flex min-w-0 items-center gap-1.5">
          <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
          <span className="shrink-0 font-medium">Sessão de Suporte WL</span>
          <span className="truncate opacity-90">
            em <span className="font-semibold">{session.tenant_name}</span>
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          <span className="font-mono tabular-nums">⏳ {remainingLabel}</span>
          {showEnterButton && (
            <Button
              size="sm"
              variant="secondary"
              className="h-6 px-2 text-[11px]"
              onClick={() => navigate("/tenant")}
              title="Voltar para o app do tenant"
            >
              <LogIn className="h-3 w-3" />
              <span className="ml-1">Acessar</span>
            </Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            className="h-6 px-2 text-[11px]"
            onClick={handleEnd}
            disabled={end.isPending}
          >
            {end.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <LogOut className="h-3 w-3" />
            )}
            <span className="ml-1">Encerrar</span>
          </Button>
          <button
            type="button"
            onClick={toggleCollapsed}
            className="rounded p-1 hover:bg-white/20"
            title="Minimizar barra da sessão de suporte"
            aria-label="Minimizar barra da sessão de suporte"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
